import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createLanguage as createLanguageAPI,
  deleteLanguage as deleteLanguageAPI,
  getLanguage,
  getLanguages,
  updateLanguage as updateLanguageAPI,
} from './languages.api';
import { Language } from './languages.types';

const name = 'languages';

const languagesEntityAdapter = createEntityAdapter<AsyncEntity<Language>>({
  selectId: language => language.code,
});

export const languagesSelectors = languagesEntityAdapter.getSelectors();

export const fetchLanguages = createAsyncThunkFromFetch(`${name}/fetchLanguages`, getLanguages);
export const fetchLanguage = createAsyncThunkFromFetch(`${name}/fetchLanguage`, getLanguage);
export const createLanguage = createAsyncThunkFromFetch(`${name}/createLanguage`, createLanguageAPI);
export const updateLanguage = createAsyncThunkFromFetch(`${name}/editLanguage`, updateLanguageAPI);
export const deleteLanguage = createAsyncThunkFromFetch(`${name}/deleteLanguage`, deleteLanguageAPI);

const languagesSlice = createSlice({
  name,
  initialState: languagesEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchLanguages.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchLanguages.fulfilled, (state, { payload }) => {
      languagesEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchLanguages.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteLanguage.pending, (state, { meta }) => {
      languagesEntityAdapter.updateOne(state, {
        id: meta.arg.languageCode,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteLanguage.fulfilled, (state, { meta }) => {
      languagesEntityAdapter.removeOne(state, meta.arg.languageCode);
    });
    builder.addCase(deleteLanguage.rejected, (state, { meta }) => {
      languagesEntityAdapter.updateOne(state, {
        id: meta.arg.languageCode,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createLanguage.pending, (state, { meta }) => {
      languagesEntityAdapter.addOne(state, { ...meta.arg, isPending: true });
    });
    builder.addCase(createLanguage.fulfilled, (state, { meta }) => {
      languagesEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: false },
      });
    });
    builder.addCase(fetchLanguage.fulfilled, (state, { payload }) => {
      languagesEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(createLanguage.rejected, (state, { meta }) => {
      languagesEntityAdapter.updateOne(state, {
        id: meta.arg.code,
        changes: { isPending: false, hasError: true },
      });
    });
  },
});

const { reducer } = languagesSlice;

export default reducer;
